import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  signalementsApi,
  SIG_CATEGORIES, SIG_STATUSES, SIG_PRIORITIES,
  CATEGORY_LABEL, STATUS_LABEL, PRIORITY_LABEL,
} from '../../services/signalementsApi';
import { getToken } from '../auth';
import '../styles/Signalements.css';
import './admin-form.css';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} · ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
}

const Signalements = () => {
  const token = getToken();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [query, setQuery] = useState('');

  const [selected, setSelected] = useState(null); // signalement courant ouvert dans le panel
  const [editing, setEditing] = useState({}); // patch en cours
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await signalementsApi.adminList(token, {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        category: filterCategory !== 'all' ? filterCategory : undefined,
        priority: filterPriority !== 'all' ? filterPriority : undefined,
        q: query || undefined,
        limit: 200,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, filterStatus, filterCategory, filterPriority, query]);

  useEffect(() => { load(); }, [load]);

  // Statistiques par statut (KPI en haut)
  const kpi = useMemo(() => {
    const c = { total: items.length, nouveau: 0, en_cours: 0, resolu: 0, urgent: 0 };
    for (const s of items) {
      if (s.status === 'nouveau') c.nouveau++;
      if (s.status === 'en_cours') c.en_cours++;
      if (s.status === 'resolu') c.resolu++;
      if (s.priority === 'urgent' && s.status !== 'resolu') c.urgent++;
    }
    return c;
  }, [items]);

  const select = (s) => {
    setSelected(s);
    setEditing({
      status: s.status,
      priority: s.priority,
      category: s.category,
      internalNote: s.internalNote || '',
      resolution: s.resolution || '',
      publicVisible: s.publicVisible,
    });
    setSaveError(null);
  };

  const closePanel = () => { setSelected(null); setEditing({}); setSaveError(null); };

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const onSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await signalementsApi.adminUpdate(token, selected.id, editing);
      setSelected(updated.signalement);
      await load();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const onQuickStatus = async (id, status) => {
    try {
      await signalementsApi.adminUpdate(token, id, { status });
      await load();
      if (selected?.id === id) {
        const r = await signalementsApi.adminGet(token, id);
        setSelected(r.signalement);
        setEditing((p) => ({ ...p, status }));
      }
    } catch (e) { alert(e.message); }
  };

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement le signalement « ${title} » ?`)) return;
    try {
      await signalementsApi.adminDelete(token, id);
      if (selected?.id === id) closePanel();
      await load();
    } catch (err) {
      alert('Suppression impossible : ' + err.message);
    }
  };

  if (!token) {
    return (
      <div className="content" style={{ padding: 40 }}>
        <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', color: 'var(--char-500)' }}>
          Session expirée. Reconnectez-vous.
        </p>
      </div>
    );
  }

  const priorityPillClass = (p) => p === 'urgent' ? 'err' : p === 'faible' ? 'muted' : 'warn';
  const statusPillClass   = (s) => {
    if (s === 'nouveau') return 'err';
    if (s === 'en_cours') return 'warn';
    if (s === 'resolu') return 'live';
    return 'muted';
  };

  return (
    <>
      <div className="topbar">
        <div className="breadcrumb">
          <span>Cambérène</span><span className="sep">/</span>
          <span>Admin</span><span className="sep">/</span>
          <b>Signalements</b>
        </div>
        <div className="search">
          <span className="ic" />
          <input
            type="text"
            placeholder="Chercher par référence, lieu, mot-clé…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <kbd>⌘ K</kbd>
        </div>
        <div className="top-actions">
          <button className="icon-btn" title="Rafraîchir" onClick={load} aria-label="Rafraîchir">↻</button>
        </div>
      </div>

      <div className="content">
        <div className="page-head">
          <div>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.22em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
              Signalements citoyens · {total} reporté{total > 1 ? 's' : ''}
            </span>
            <h1>Les signalements,<br /><span style={{ fontStyle: 'italic' }}>en gestion.</span></h1>
            <p className="sub">Voirie, propreté, éclairage, eau, sécurité — traitement des problèmes remontés par les habitants.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
              {SIG_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.lbl}</option>)}
            </select>
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
              className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
              {SIG_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.lbl}</option>)}
            </select>
            <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
              className="pill-btn" style={{ background: 'var(--bone)', border: '1px solid var(--line)', padding: '7px 14px' }}>
              {SIG_PRIORITIES.map((p) => <option key={p.id} value={p.id}>{p.lbl}</option>)}
            </select>
          </div>
        </div>

        {/* KPI bandeau */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { lbl: 'Nouveaux',  val: kpi.nouveau,  cls: 'err'  },
            { lbl: 'En cours',  val: kpi.en_cours, cls: 'warn' },
            { lbl: 'Résolus',  val: kpi.resolu,   cls: 'live' },
            { lbl: 'Urgents',   val: kpi.urgent,   cls: 'err'  },
          ].map((k) => (
            <div key={k.lbl} className="kpi">
              <span className="lab">
                <span className="dot" style={{ background: k.cls === 'err' ? '#E63946' : k.cls === 'warn' ? '#E8B53C' : k.cls === 'live' ? '#34A853' : 'var(--char-400)' }} />
                {k.lbl}
              </span>
              <b>{k.val}</b>
            </div>
          ))}
        </div>

        {error && (
          <div className="bulkbar on" style={{ background: 'rgba(230,57,70,.12)', color: '#B72333' }}>
            <span>⚠</span><span>Erreur : {error}</span>
          </div>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Titre / Lieu</th>
                <th>Catégorie</th>
                <th>Priorité</th>
                <th>Statut</th>
                <th>Reporter</th>
                <th>Reçu</th>
                <th style={{ width: 140 }} />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>Chargement…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
                  Aucun signalement.
                </td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} onClick={() => select(s)} className={selected?.id === s.id ? 'selected' : ''}>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.06em', color: 'var(--char-700)', fontWeight: 500 }}>{s.reference}</td>
                  <td>
                    <b style={{ fontWeight: 500, fontSize: 14 }}>{s.title}</b>
                    {s.location && <small style={{ display: 'block', marginTop: 3, fontSize: 12, color: 'var(--char-500)' }}>📍 {s.location}</small>}
                  </td>
                  <td><span className="cat-pill">{CATEGORY_LABEL[s.category] || s.category}</span></td>
                  <td><span className={`status-pill ${priorityPillClass(s.priority)}`}>{PRIORITY_LABEL[s.priority] || s.priority}</span></td>
                  <td><span className={`status-pill ${statusPillClass(s.status)}`}>{STATUS_LABEL[s.status] || s.status}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--char-700)' }}>
                    {s.anonymous
                      ? <span style={{ color: 'var(--char-500)', fontStyle: 'italic' }}>anonyme</span>
                      : (
                        <>
                          <b style={{ fontWeight: 500 }}>{s.reporterName || '—'}</b>
                          {s.reporterEmail && <small style={{ display: 'block', fontSize: 11, color: 'var(--char-500)' }}>{s.reporterEmail}</small>}
                        </>
                      )}
                  </td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-500)' }}>{fmtDate(s.createdAt)}</td>
                  <td onClick={(ev) => ev.stopPropagation()}>
                    <div className="row-actions" style={{ opacity: 1 }}>
                      {s.status === 'nouveau' && (
                        <button title="Prendre en charge" onClick={() => onQuickStatus(s.id, 'en_cours')}>▶</button>
                      )}
                      {s.status === 'en_cours' && (
                        <button title="Marquer résolu" onClick={() => onQuickStatus(s.id, 'resolu')} style={{ background: '#34A853', color: 'var(--bone)', borderColor: '#34A853' }}>✓</button>
                      )}
                      <button title="Détails / Éditer" onClick={() => select(s)}>✎</button>
                      <button title="Supprimer" className="danger" onClick={() => onDelete(s.id, s.title)}>✕</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-foot">
            <span>{total} signalement{total > 1 ? 's' : ''}</span>
            <span>{items.length} affiché{items.length > 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>

      {/* ============ PANEL LATÉRAL D'INSTRUCTION ============ */}
      {selected && (
        <div className="admin-modal-bg" onClick={closePanel}>
          <div className="admin-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="admin-modal-head">
              <div>
                <span className="admin-modal-eyebrow">
                  {selected.reference} · {fmtDateTime(selected.createdAt)}
                </span>
                <h2 className="admin-modal-title">{selected.title}</h2>
              </div>
              <button onClick={closePanel} className="admin-modal-close" aria-label="Fermer">✕</button>
            </div>

            {/* Description du citoyen (read-only) */}
            <div style={{
              padding: '14px 16px', borderRadius: 10,
              background: 'var(--bone-2)', border: '1px solid var(--line)',
              marginBottom: 16,
            }}>
              <small style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--char-500)' }}>
                Description du citoyen
              </small>
              <p style={{ margin: '8px 0 0', fontSize: 14, lineHeight: 1.55, color: 'var(--char-900)' }}>{selected.description}</p>
              {selected.location && (
                <p style={{ margin: '10px 0 0', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.06em', color: 'var(--char-700)' }}>
                  📍 {selected.location}
                </p>
              )}
            </div>

            {/* Identité reporter */}
            <div style={{
              padding: '12px 16px', borderRadius: 10,
              background: selected.anonymous ? 'var(--bone-2)' : 'rgba(231,111,81,.06)',
              border: '1px solid var(--line)',
              marginBottom: 16, fontSize: 13,
            }}>
              {selected.anonymous ? (
                <span style={{ color: 'var(--char-500)', fontStyle: 'italic' }}>
                  Signalement anonyme — pas de coordonnées du reporter.
                </span>
              ) : (
                <>
                  <b>{selected.reporterName || '—'}</b>
                  {selected.reporterEmail && <small style={{ display: 'block', marginTop: 4, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{selected.reporterEmail}</small>}
                  {selected.reporterPhone && <small style={{ display: 'block', marginTop: 2, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--char-700)' }}>{selected.reporterPhone}</small>}
                </>
              )}
            </div>

            {/* Formulaire d'instruction */}
            <div className="admin-form">
              <div className="admin-form-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <label className="admin-field">
                  <span className="admin-field-label">Statut</span>
                  <select
                    value={editing.status}
                    onChange={(e) => setEditing((p) => ({ ...p, status: e.target.value }))}
                  >
                    {SIG_STATUSES.filter((s) => s.id !== 'all').map((s) =>
                      <option key={s.id} value={s.id}>{s.lbl}</option>
                    )}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Priorité</span>
                  <select
                    value={editing.priority}
                    onChange={(e) => setEditing((p) => ({ ...p, priority: e.target.value }))}
                  >
                    {SIG_PRIORITIES.filter((p) => p.id !== 'all').map((p) =>
                      <option key={p.id} value={p.id}>{p.lbl}</option>
                    )}
                  </select>
                </label>
                <label className="admin-field">
                  <span className="admin-field-label">Catégorie</span>
                  <select
                    value={editing.category}
                    onChange={(e) => setEditing((p) => ({ ...p, category: e.target.value }))}
                  >
                    {SIG_CATEGORIES.filter((c) => c.id !== 'all').map((c) =>
                      <option key={c.id} value={c.id}>{c.lbl}</option>
                    )}
                  </select>
                </label>
              </div>

              <label className="admin-field">
                <span className="admin-field-label">Note interne (non visible publiquement)</span>
                <textarea
                  rows={2}
                  value={editing.internalNote}
                  onChange={(e) => setEditing((p) => ({ ...p, internalNote: e.target.value }))}
                  placeholder="Ex : transmis au service technique, devis demandé, etc."
                />
              </label>

              <label className="admin-field">
                <span className="admin-field-label">Résolution (visible publiquement si statut = résolu)</span>
                <textarea
                  rows={2}
                  value={editing.resolution}
                  onChange={(e) => setEditing((p) => ({ ...p, resolution: e.target.value }))}
                  placeholder="Ex : Joint remplacé par l'équipe technique le 2026-05-21."
                />
              </label>

              <label className="admin-publish-toggle">
                <input
                  type="checkbox"
                  checked={editing.publicVisible}
                  onChange={(e) => setEditing((p) => ({ ...p, publicVisible: e.target.checked }))}
                />
                <span>{editing.publicVisible ? 'Visible publiquement sur la liste' : 'Caché du public (sensible)'}</span>
              </label>

              {selected.resolvedAt && (
                <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.06em', color: 'var(--char-500)' }}>
                  Résolu le {fmtDateTime(selected.resolvedAt)} par admin · {selected.resolvedBy?.slice(0, 8) || '—'}
                </div>
              )}

              {saveError && <div className="admin-form-error">{saveError}</div>}

              <div className="admin-form-foot">
                <button type="button" onClick={closePanel} className="admin-btn">Fermer</button>
                <button type="button" onClick={onSave} disabled={saving} className="admin-btn admin-btn-primary">
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Signalements;
